import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import * as React from "react";
import {
  SlackAppVersion,
  SlackConnectionDTO,
  useGroopitApiContext,
} from "groopit-js";
import useLatestRef from "../react/react-use/useLatestRef";

interface SlackConnectionSelectorProps {
  connections: SlackConnectionDTO[];
  excludedConnections: string[];
  minRequiredAppVersion: SlackAppVersion;
  minRequiredUserVersion: SlackAppVersion;
  allowNew: boolean;
  onConnectionSelected: (connection: SlackConnectionDTO) => void;
}

export const SlackConnectionSelector = (
  props: SlackConnectionSelectorProps,
): JSX.Element => {
  const { excludedConnections, minRequiredUserVersion, onConnectionSelected } =
    props;
  const api = useLatestRef(useGroopitApiContext());
  const [selectedConnection, setSelectedConnection] =
    React.useState<SlackConnectionDTO>();
  const [userVersion, setUserVersion] = React.useState<SlackAppVersion>();

  const onWorkspaceSelected = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    if (event.target.value === "new-workspace") {
      installOrUpgradeApp(null);
    } else {
      setSelectedConnection(
        props.connections.find((c) => c.id === event.target.value),
      );
    }
  };

  const installOrUpgradeApp = (workspaceId: string | null) => {
    window.location.href = `/slack/install?team=${
      workspaceId ? workspaceId : ""
    }&redirectUri=${encodeURIComponent(window.location.href)}`;
  };

  const linkOrUpgradeUser = (workspaceId: string) => {
    window.location.href = `/slack/link?team=${
      workspaceId ? workspaceId : ""
    }&redirectUri=${encodeURIComponent(window.location.href)}`;
  };

  React.useEffect(() => {
    if (
      selectedConnection &&
      excludedConnections.includes(selectedConnection.id)
    ) {
      setSelectedConnection(undefined);
    }

    if (
      !selectedConnection &&
      props.connections.length == 1 &&
      !excludedConnections.includes(props.connections[0].id)
    ) {
      setSelectedConnection(props.connections[0]);
    }
  }, [props.connections, excludedConnections, selectedConnection]);

  React.useEffect(() => {
    if (selectedConnection) {
      setUserVersion(undefined);
      api.current.slack
        .getUserVersion(selectedConnection.id)
        .then(setUserVersion)
        .catch((error) => {
          console.log(error);
        });
    }
  }, [api, selectedConnection]);

  React.useEffect(() => {
    if (
      selectedConnection &&
      userVersion !== undefined &&
      userVersion >= minRequiredUserVersion
    ) {
      onConnectionSelected(selectedConnection);
    }
  }, [
    userVersion,
    minRequiredUserVersion,
    selectedConnection,
    onConnectionSelected,
  ]);

  return (
    <>
      {props.connections.length > 0 ? (
        <div className="space-y-2">
          <FormControl fullWidth>
            <InputLabel id="select-workspace-label">
              Select Slack Workspace:
            </InputLabel>
            <Select
              labelId="select-workspace-label"
              id="select-workspace-select"
              value={selectedConnection?.id ?? ""}
              onChange={onWorkspaceSelected}
            >
              {props.connections.map((connection) => (
                <MenuItem
                  key={connection.id}
                  value={connection.id}
                  disabled={excludedConnections.includes(connection.id)}
                >
                  <div className="flex gap-2 items-center">
                    <img
                      className="filter-app-black h-4"
                      src="/Content/images/ic_slack.png"
                    />
                    <div>{connection.name}</div>
                  </div>
                </MenuItem>
              ))}
              {props.allowNew && (
                <MenuItem key="new-workspace" value="new-workspace">
                  + New Organization
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {selectedConnection && (
            <>
              {selectedConnection.version >= props.minRequiredAppVersion ? (
                userVersion !== undefined ? (
                  userVersion < minRequiredUserVersion &&
                  (minRequiredUserVersion <= selectedConnection.version ? (
                    <button
                      className="btn btn-primary"
                      onClick={() => linkOrUpgradeUser(selectedConnection.id)}
                    >
                      Sign-in to Slack
                    </button>
                  ) : (
                    <>
                      <p className="my-4">
                        This feature requires the latest version of the Slack
                        app. Please update and try again.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={() =>
                          installOrUpgradeApp(selectedConnection.id)
                        }
                      >
                        Update
                      </button>
                    </>
                  ))
                ) : (
                  <div className="text-center mt-8">
                    <CircularProgress />
                  </div>
                )
              ) : (
                <>
                  <p className="my-4">
                    This feature requires the latest version of the Slack app.
                    Please update and try again.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => installOrUpgradeApp(selectedConnection.id)}
                  >
                    Update
                  </button>
                </>
              )}
            </>
          )}
        </div>
      ) : (
        <button
          className="btn btn-primary"
          onClick={() => installOrUpgradeApp(null)}
        >
          Sign-in to Slack
        </button>
      )}
    </>
  );
};
